import { AdSurgeLogoText, ArrowDown, Check, Income, ReportUp, SearchBold } from '@tencent/odc-iconpack';
import { Button, DropMenu, GlobalSearchModal, LocaleProvider, message } from 'odc-shared/components';
import { ELanguage, useLocale } from 'odc-shared/i18n';
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';

import { ModuleSupportCenterCatalog } from '@src/api/generated/data-contracts';
import CopyRight from '@src/components/CopyRight';
import website from '@src/constants/website';
import useMobxActions from '@src/hooks/useMobxActions';
import useMobxSelector from '@src/hooks/useMobxSelector';
import { applyHelpCenterLanguage, pickEnabledLanguage, rememberHelpCenterLanguage } from '@src/i18n';
import { IArticle } from '@src/types/support.type';

import Content from './components/content';
import GettingStarted from './components/gettingStarted';
import LanguageSwitcher from './components/languageSwitcher';
import Menu from './components/menu';
import { isSingleLanguage } from './constants/language';
import useSearch from './hooks/useSearch';
import useSupportSearch from './hooks/useSupportSearch';

import './index.scss';

message.config({
  top: 80,
});

/**
 * 将 ModuleSupportCenterCatalog 转换为 IArticle 格式
 */
const transformCatalogToArticle = (catalog: ModuleSupportCenterCatalog): IArticle => {
  const hasChildren = catalog.children && catalog.children.length > 0;
  const isLeafWithPost = catalog.postId && !hasChildren;
  return {
    name: catalog.name || 'Untitled',
    aid: catalog.postId || catalog.id,
    // 只有叶子节点且有 postId 时才设置 article 字段
    article: isLeafWithPost ? catalog.postId : undefined,
    // 如果有子节点，递归转换
    subList: hasChildren ? catalog.children?.map(transformCatalogToArticle) : undefined,
  };
};

/**
 * 递归收集所有有子节点的目录 ID，用于默认展开
 */
const collectExpandableIds = (catalogsMap: Map<string, ModuleSupportCenterCatalog>, pid?: string): string[] => {
  const ids: string[] = [];
  if (!pid) {
    return ids;
  }
  const traverse = (id: string) => {
    const parentId = catalogsMap.get(id)?.parentId;
    if (parentId) {
      ids.push(parentId);
      traverse(parentId);
    }
  };
  traverse(pid);
  return ids;
};

// 是否为聚合平台模式（仅通过 URL 显式进入，UI 不可主动切换）
const isMediation = (platform?: string): boolean => platform === 'mediation';

// 平台触发器（顶部"Monetization / User Growth"那个胶囊）显示文案
const getPlatformTriggerLabel = (platform?: string): string => {
  if (isMediation(platform)) return 'Mediation';
  if (platform === 'advertiser') return 'User Growth';
  return 'Monetization';
};

const SupportCenter = () => {
  const navigate = useNavigate();
  const actions = useMobxActions();

  // 文档类型相关
  const { params, updateParams } = useSearch();
  const { t } = useLocale('helpCenter');
  const [openCategory, setOpenCategory] = useState<boolean>(false);
  const mediationMode = isMediation(params.platform);

  // 三平台一致：内容语言只由 URL 决定（缺省 en，未启用语言已归一化）；聚合平台同样可切中文
  const language = params.language;
  // 只启用一种语言时不展示语言入口
  const showLanguageEntry = !isSingleLanguage();

  /**
   * 界面语言（i18next）跟着 URL 走
   *
   * 界面文案与内容语言必须是同一个来源：**只由 `?language=` 决定（缺省英文）**。
   * 之前界面取本地偏好（i18next 启动时的值）、内容取 URL，两者能不一致 ——
   * 分享链接（URL=zh、本地偏好=en）会出现"界面全英文 + 内容中文 + 下拉显示中文"。
   *
   * 用 useLayoutEffect 而不是 useEffect：必须在这次绘制前切完，否则会出现
   * "URL 已到英文、菜单文案已英文，目录名/标题还是中文"的一帧错位（router 的地址更新在
   * startTransition 里，比同步的 changeLanguage 晚一帧落地）。
   *
   * 只切 i18next：语言本身由 URL 承载，不写任何本地存储。
   */
  useLayoutEffect(() => {
    applyHelpCenterLanguage(language);
  }, [language]);

  /**
   * 切换语言：只改 URL，界面文案（i18next）由上面的 useLayoutEffect 跟着 language 切。
   *
   * 不在点击时同步切 i18next：那会比走过渡的路由更新早一帧生效，出现"文案已英文、
   * 目录名还是中文"的一帧错位 —— 两者必须同一帧落地。
   */
  const handleLanguageChange = useCallback((next: ELanguage) => {
    const applied = pickEnabledLanguage(next);
    // 记下这次选择：只用于下次进入帮助中心（/supportcenter 不带 language）时补 URL，语言本身仍以 URL 为准
    rememberHelpCenterLanguage(applied);
    updateParams({ language: applied });
  }, [updateParams]);

  // 使用搜索 hook
  const {
    searchState,
    searchSuggestions,
    searchResults,
    showGlobalSearch,
    setShowGlobalSearch,
    handleSearchChange,
    handleSearchClose,
    handleViewMore,
  } = useSupportSearch(language);

  // 从 store 获取目录数据
  const catalogsByPlatform = useMobxSelector((stores) => stores.helpCenterStore.catalogsByPlatform);
  const catalogsMap = useMobxSelector((stores) => stores.helpCenterStore.catalogsMap);
  // 已缓存目录对应的语言（store 的缓存判定依据），用来判断页面上的目录数据是不是当前语言那一份
  const catalogsLanguage = useMobxSelector((stores) => stores.helpCenterStore.catalogsLanguage);

  const platform = params.platform || 'publisher';

  // 目录数据是不是「当前语言」那一份：store 按语言整棵覆盖，切语言 / 从首页内页进文章时
  // 手上还可能是上一份（冷启动则一份都没有），未就绪就渲染会闪上一个语言的名字
  const catalogsReady = catalogsLanguage[platform] === language;

  // mediation 平台目录按需拉取（语言与另两平台同口径，切语言要按新语言重拉；错误提示跟随 i18n）
  useEffect(() => {
    if (mediationMode) {
      actions.helpCenterStore.fetchCatalogs('mediation', { language, i18nErrorText: true });
    }
  }, [mediationMode, language, actions]);

  // 语言没对齐时不给数据（交给 Menu 出骨架），避免先闪一眼上一个语言的目录名
  const navData = useMemo(() => {
    if (!catalogsReady) return [];
    return (catalogsByPlatform[platform] || []).map(transformCatalogToArticle);
  }, [catalogsByPlatform, platform, catalogsReady]);

  // 当前文章祖先链（默认展开用）：目录到货才算，否则会拿旧语言的树算错；渲染期派生，effect 里读的是快照
  const defaultExpandedIds = useMemo(
    () => (catalogsReady ? collectExpandableIds(catalogsMap, params.pid) : []),
    [catalogsMap, params.pid, catalogsReady],
  );

  // 有 pid 就渲染正文（正文自带骨架），只有"数据已是当前语言 + pid 不在树里"才回默认页
  const showArticle = !!params.pid && (!catalogsReady || catalogsMap.has(params.pid));

  return (
    // 共享组件（搜索弹窗 / 按钮等）的文案跟随帮助中心语言；首页不包，保持英文
    <LocaleProvider lang={language}>
      <div className="odc-support-center">
        <div className="support-center-header">
          <div className="official-logo">
            <AdSurgeLogoText className="logo-brand"
              onClick={() => {
                if (mediationMode) {
                  window.location.href = website.mediation;
                  return;
                }
                navigate('/');
              }}
            />
            <div className="!ml-3r">
              {mediationMode ? (
              // 聚合平台模式：仅展示标题，不允许切换（下拉中也不暴露 mediation 选项）
                <div className="odc-docs-category-trigger odc-docs-category-trigger-static !px-3r w-288">
                  {getPlatformTriggerLabel(params.platform)}
                </div>
              ) : (
                <DropMenu
                  data={[
                    {
                      value: 'advertiser',
                      label: (<div className="odc-category-dropmenu-item">
                        User growth
                        {params.platform === 'advertiser' && <Check className="!text-md" />}
                      </div>
                      ),
                    },
                    {
                      value: 'publisher',
                      label: (<div className="odc-category-dropmenu-item">
                        Monetization
                        {params.platform === 'publisher' && <Check className="!text-md" />}
                      </div>),
                    },
                  ]}
                  offsetData={{
                    top: -8,
                  }}
                  dropdownPortalClassName="odc-docs-category-dropmenu"
                  onClick={(value: number | string) => {
                    updateParams({ platform: value as 'publisher' | 'advertiser', pid: '' });
                    setOpenCategory(false);
                  }}
                  open={openCategory}
                  onOpen={(next) => setOpenCategory(next)}
                  trigger="click"
                  matchTriggerWidth
                >
                  <div
                    className="odc-docs-category-trigger !px-3r w-148"
                    onClick={() => setOpenCategory(true)}
                  >
                    {getPlatformTriggerLabel(params.platform)}
                    <ArrowDown className="!ml-3r text-base" />
                  </div>
                </DropMenu>
              )}
            </div>
          </div>
          <div className="options">
            {
            !mediationMode && (
              <div className="help-center-search" onClick={() => setShowGlobalSearch(true)}>
                <div className="help-center-search-icon">
                  <SearchBold />
                </div>
                <div className="help-center-search-placeholder">{t('Search by topic or keyword')}</div>
              </div>
            )
          }
            {
            mediationMode ? (
              <Button type="primary" onClick={() => window.location.href = website.mediation}>
                {t('Log in')}
              </Button>
            ) : (
              <DropMenu
                data={[
                  { value: 'userGrowth', label: (<div className="odc-platform-item"><div className="odc-platform-icon"><ReportUp /></div>User Growth</div>) },
                  { value: 'monetization', label: (<div className="odc-platform-item"><div className="odc-platform-icon"><Income /></div>Monetization</div>) },
                ]}
                offsetData={{
                  top: 0,
                }}
                dropdownPortalClassName="odc-login-dropmenu"
                onClick={(value: number | string) => {
                  if (value === 'userGrowth') {
                    window.location.href = website.userGrowth;
                  } else if (value === 'monetization') {
                    window.location.href = website.monetization;
                  }
                }}
                trigger="hover"
              >
                <Button type="primary">
                  {t('Log in')}
                </Button>
              </DropMenu>
            )
          }
            {/* 语言入口：未启用中文（语言列表只有 en）时，界面与内容的语言入口一起隐藏 */}
            {showLanguageEntry && (
            <LanguageSwitcher value={language} onChange={handleLanguageChange} />
            )}
          </div>
        </div>
        <div className="support-center-main">
          <div className="support-center-menu">
            <Menu
              navData={navData}
              ready={catalogsReady}
              defaultExpandedIds={defaultExpandedIds}
              activeId={params.pid}
              setPid={(pid: string) => updateParams({ pid })}
              platform={platform}
            />
          </div>
          <div className="support-center-content">
            {
            showArticle ? (
              <Content
                key={`${params.pid}-${language}`}
                pid={params.pid}
                // 标题同样取自目录树：语言没对齐时先留空（正文骨架屏在，标题位不留旧语言的名字）
                title={catalogsReady ? (catalogsMap.get(params.pid)?.name || '') : ''}
                language={language}
              />
            ) : (
              <GettingStarted
                platform={platform}
                ready={catalogsReady}
                setPid={(pid: string | undefined) => updateParams({ pid })}
              />
            )
          }
          </div>
        </div>
        <CopyRight isMediation={mediationMode} localized />
        <GlobalSearchModal
          type="panel"
          escTag={true}
          placeholder={t('Search by topic or keyword')}
          show={showGlobalSearch}
          value={searchState.keyword}
          suggestions={searchSuggestions}
          results={searchResults}
          resultsCount={searchState.total}
          onChange={handleSearchChange}
          onClose={handleSearchClose}
          onViewMore={handleViewMore}
        />
      </div>
    </LocaleProvider>
  );
};
export default SupportCenter;